/**
 * Copyright (c) 2022-2026 Podstack.ai
 * All rights reserved.
 * Proprietary and Confidential.
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 * This file is part of the Podstack Platform.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { vmApi } from '../../services/vmApi';
import { VMFirewallPort, VMFirewallPortCreate, VMFirewallPortUpdate } from '../../types/vm';
import toast from 'react-hot-toast';

interface VMFirewallPortsProps {
  vmId: string;
  isRunning: boolean;
}

const VMFirewallPorts: React.FC<VMFirewallPortsProps> = ({ vmId, isRunning }) => {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<VMFirewallPort | null>(null);
  const [editingPort, setEditingPort] = useState<VMFirewallPortCreate>({
    port: 8080,
    protocol: 'TCP',
    name: '',
    allowed_source_ips: [],
    description: '',
  });
  const [newSourceIp, setNewSourceIp] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const { data: portsData, isLoading } = useQuery({
    queryKey: ['vm-firewall-ports', vmId],
    queryFn: () => vmApi.getVMFirewallPorts(vmId),
    enabled: !!vmId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const ports = portsData?.ports || [];

  const addPortMutation = useMutation({
    mutationFn: (portConfig: VMFirewallPortCreate) => {
      console.log('Adding firewall port:', { vmId, portConfig });
      return vmApi.addVMFirewallPort(vmId, portConfig);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vm-firewall-ports', vmId] });
      setShowAddModal(false);
      setEditingPort({
        port: 8080,
        protocol: 'TCP',
        name: '',
        allowed_source_ips: [],
        description: '',
      });
      toast.success('Firewall port added successfully');
    },
    onError: (error: any) => {
      console.error('Failed to add firewall port:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
      });
      const errorMessage = error.response?.data?.detail || error.response?.data?.error || error.message || 'Failed to add firewall port';
      toast.error(errorMessage);
    },
  });

  const updatePortMutation = useMutation({
    mutationFn: ({ portId, updates }: { portId: string; updates: VMFirewallPortUpdate }) =>
      vmApi.updateVMFirewallPort(vmId, portId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vm-firewall-ports', vmId] });
      setShowEditModal(null);
      toast.success('Firewall port updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update firewall port');
    },
  });

  const deletePortMutation = useMutation({
    mutationFn: (portId: string) => vmApi.deleteVMFirewallPort(vmId, portId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vm-firewall-ports', vmId] });
      toast.success('Firewall port deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete firewall port');
    },
  });

  const validateCIDR = (cidr: string): boolean => {
    const cidrRegex = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;
    if (!cidrRegex.test(cidr)) return false;
    
    const [ip, prefix] = cidr.split('/');
    const prefixNum = parseInt(prefix, 10);
    if (prefixNum < 0 || prefixNum > 32) return false;
    
    const parts = ip.split('.').map(Number);
    return parts.every(part => part >= 0 && part <= 255);
  };

  const handleAddSourceIp = () => {
    if (!newSourceIp.trim()) {
      toast.error('Please enter a CIDR block (e.g., 10.0.0.0/8)');
      return;
    }

    if (!validateCIDR(newSourceIp.trim())) {
      toast.error('Invalid CIDR format. Use format: 10.0.0.0/8');
      return;
    }

    const currentIps = editingPort.allowed_source_ips || [];
    if (currentIps.includes(newSourceIp.trim())) {
      toast.error('This IP block is already added');
      return;
    }

    setEditingPort({
      ...editingPort,
      allowed_source_ips: [...currentIps, newSourceIp.trim()],
    });
    setNewSourceIp('');
  };

  const handleRemoveSourceIp = (ip: string) => {
    setEditingPort({
      ...editingPort,
      allowed_source_ips: (editingPort.allowed_source_ips || []).filter(i => i !== ip),
    });
  };

  const handleAddPort = () => {
    console.log('handleAddPort called with:', editingPort);
    setValidationError(null);
    
    // Validate port
    if (!editingPort.port || editingPort.port < 1 || editingPort.port > 65535) {
      const error = 'Port must be between 1 and 65535';
      setValidationError(error);
      toast.error(error);
      return;
    }

    // Validate name
    if (!editingPort.name || !editingPort.name.trim()) {
      const error = 'Port name is required';
      setValidationError(error);
      toast.error(error);
      return;
    }

    // Ensure protocol is uppercase
    // Send undefined for empty arrays (backend interprets as "allow all")
    const portConfig: VMFirewallPortCreate = {
      port: editingPort.port,
      protocol: (editingPort.protocol || 'TCP').toUpperCase() as 'TCP' | 'UDP',
      name: editingPort.name.trim(),
      allowed_source_ips: editingPort.allowed_source_ips && editingPort.allowed_source_ips.length > 0 
        ? editingPort.allowed_source_ips 
        : undefined, // undefined = allow all sources
      description: editingPort.description && editingPort.description.trim() 
        ? editingPort.description.trim() 
        : undefined,
    };
    
    // Remove undefined fields to avoid sending them in the request
    const cleanedConfig: any = { ...portConfig };
    if (cleanedConfig.allowed_source_ips === undefined) {
      delete cleanedConfig.allowed_source_ips;
    }
    if (cleanedConfig.description === undefined) {
      delete cleanedConfig.description;
    }

    console.log('[VMFirewallPorts] Calling addPortMutation with cleaned config:', cleanedConfig);
    console.log('[VMFirewallPorts] VM ID:', vmId);
    
    // Call mutation - errors are handled in onError callback
    addPortMutation.mutate(cleanedConfig);
  };

  const handleUpdatePort = () => {
    if (!showEditModal) return;

    const updates: VMFirewallPortUpdate = {
      allowed_source_ips: editingPort.allowed_source_ips,
      description: editingPort.description,
    };

    updatePortMutation.mutate({ portId: showEditModal.id, updates });
  };

  const handleDeletePort = (port: VMFirewallPort) => {
    if (port.port === 22 && isRunning) {
      toast.error('Cannot remove SSH port (22) while VM is running with SSH enabled');
      return;
    }

    if (window.confirm(`Are you sure you want to remove port ${port.port}/${port.protocol}?`)) {
      deletePortMutation.mutate(port.id);
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-10 bg-gray-700 rounded"></div>
            <div className="h-10 bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheckIcon className="w-5 h-5 text-green-400" />
          <h3 className="text-lg font-semibold text-white">Firewall & Ports</h3>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-3 py-1.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-gray-950 transition-colors flex items-center gap-1.5"
        >
          <PlusIcon className="w-4 h-4" />
          Add Port
        </button>
      </div>

      <div>
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-3"></div>
            <p className="text-gray-400">Loading firewall ports...</p>
          </div>
        ) : ports.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <ShieldCheckIcon className="h-12 w-12 mx-auto mb-3 text-gray-500" />
            <p className="text-white">No firewall ports configured</p>
            <p className="text-sm mt-1 text-gray-400">Default ports (22, 80, 443) should be automatically created. If not visible, please refresh.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Port
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Protocol
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Allowed Source IPs
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-900 divide-y divide-gray-700">
                {ports.map((port) => {
                  const isDefaultPort = [22, 80, 443].includes(port.port) && port.protocol === 'TCP';
                  return (
                  <tr key={port.id} className={isDefaultPort ? 'bg-gray-800/50' : ''}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-white">
                      <div className="flex items-center gap-2">
                        {port.port}
                        {isDefaultPort && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                            Default
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                      {port.protocol}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                      {port.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {port.allowed_source_ips && port.allowed_source_ips.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {port.allowed_source_ips.map((ip, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-500/20 text-blue-400"
                            >
                              {ip}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">Allow all</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {port.description || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setShowEditModal(port);
                          setEditingPort({
                            port: port.port,
                            protocol: port.protocol,
                            name: port.name,
                            allowed_source_ips: port.allowed_source_ips || [],
                            description: port.description || '',
                          });
                        }}
                        className="text-indigo-400 hover:text-indigo-300 mr-3"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeletePort(port)}
                        className="text-red-400 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isDefaultPort && port.port === 22}
                        title={isDefaultPort && port.port === 22 ? "Cannot delete SSH port (22) - it's required for VM access" : "Delete port"}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Port Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={() => setShowAddModal(false)}></div>
            
            <div className="inline-block align-bottom bg-gray-900 border border-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-gray-900 px-4 pt-4 pb-4 sm:p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-white">Add Firewall Port</h3>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-300"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAddPort();
                  }}
                  className="space-y-3"
                >
                  {validationError && (
                    <div className="bg-red-500/20 border border-red-600 rounded-md p-2.5">
                      <p className="text-xs text-red-400">{validationError}</p>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">
                      Port *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="65535"
                      value={editingPort.port || ''}
                      onChange={(e) => {
                        const portValue = parseInt(e.target.value) || 0;
                        setEditingPort({ ...editingPort, port: portValue });
                        setValidationError(null);
                      }}
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-600 bg-gray-800 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">
                      Protocol *
                    </label>
                    <select
                      value={editingPort.protocol}
                      onChange={(e) =>
                        setEditingPort({
                          ...editingPort,
                          protocol: e.target.value as 'TCP' | 'UDP',
                        })
                      }
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-600 bg-gray-800 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="TCP">TCP</option>
                      <option value="UDP">UDP</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={editingPort.name || ''}
                      onChange={(e) => {
                        setEditingPort({ ...editingPort, name: e.target.value });
                        setValidationError(null);
                      }}
                      placeholder="e.g., ssh, http, api"
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-600 bg-gray-800 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">
                      Allowed Source IPs (CIDR blocks)
                    </label>
                    <p className="text-xs text-gray-400 mb-1.5">
                      Leave empty to allow all. Add CIDR blocks (e.g., 10.0.0.0/8) to restrict.
                    </p>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={newSourceIp}
                        onChange={(e) => setNewSourceIp(e.target.value)}
                        placeholder="10.0.0.0/8"
                        className="flex-1 px-2.5 py-1.5 text-sm border border-gray-600 bg-gray-800 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddSourceIp();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleAddSourceIp}
                        className="px-3 py-1.5 border border-gray-600 rounded-md text-xs font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 whitespace-nowrap"
                      >
                        Add
                      </button>
                    </div>
                    {editingPort.allowed_source_ips && editingPort.allowed_source_ips.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {editingPort.allowed_source_ips.map((ip, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-500/20 text-blue-400"
                          >
                            {ip}
                            <button
                              type="button"
                              onClick={() => handleRemoveSourceIp(ip)}
                              className="ml-1 text-blue-400 hover:text-blue-300"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-800 flex justify-between gap-2">
                    <button
                      type="submit"
                      disabled={addPortMutation.isPending}
                      className="px-3 py-1.5 rounded-md border border-transparent text-sm font-medium text-white bg-primary-500 hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 transition-colors"
                    >
                      {addPortMutation.isPending ? 'Adding...' : 'Add Port'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="px-3 py-1.5 rounded-md border border-gray-600 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Port Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-black bg-opacity-50" onClick={() => setShowEditModal(null)}></div>
            
            <div className="inline-block align-bottom bg-gray-900 border border-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-gray-900 px-4 pt-4 pb-4 sm:p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-white">
                    Edit Firewall Port ({showEditModal.port}/{showEditModal.protocol})
                  </h3>
                  <button
                    onClick={() => setShowEditModal(null)}
                    className="text-gray-400 hover:text-gray-300"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1.5">
                      Allowed Source IPs (CIDR blocks)
                    </label>
                    <p className="text-xs text-gray-400 mb-1.5">
                      Leave empty to allow all. Add CIDR blocks (e.g., 10.0.0.0/8) to restrict.
                    </p>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={newSourceIp}
                        onChange={(e) => setNewSourceIp(e.target.value)}
                        placeholder="10.0.0.0/8"
                        className="flex-1 px-2.5 py-1.5 text-sm border border-gray-600 bg-gray-800 text-white rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddSourceIp();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleAddSourceIp}
                        className="px-3 py-1.5 border border-gray-600 rounded-md text-xs font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 whitespace-nowrap"
                      >
                        Add
                      </button>
                    </div>
                    {editingPort.allowed_source_ips && editingPort.allowed_source_ips.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {editingPort.allowed_source_ips.map((ip, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-500/20 text-blue-400"
                          >
                            {ip}
                            <button
                              type="button"
                              onClick={() => handleRemoveSourceIp(ip)}
                              className="ml-1 text-blue-400 hover:text-blue-300"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-800 flex justify-between gap-2">
                  <button
                    type="button"
                    onClick={handleUpdatePort}
                    disabled={updatePortMutation.isPending}
                    className="px-3 py-1.5 rounded-md border border-transparent text-sm font-medium text-white bg-primary-500 hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 transition-colors"
                  >
                    {updatePortMutation.isPending ? 'Updating...' : 'Update Port'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEditModal(null)}
                    className="px-3 py-1.5 rounded-md border border-gray-600 text-sm font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VMFirewallPorts;
